/**
 * User-facing text for an axios failure.
 * A JSON message from the CMS is shown as sent.
 * An HTML body or a missing response includes the status and URL so the
 * real failure is visible.
 *
 * @returns {string|null} User-facing message, or null when the failure should not trigger a popup.
 */
function responseDetail(data) {
  if (data == null || data === "") return "";
  if (typeof data === "string") {
    return data.replace(/<[^>]+>/g, " ").replace(/\s+/g, " ").trim();
  }
  if (typeof data === "object") {
    const text = data.message || data.error || "";
    return typeof text === "string" ? text : "";
  }
  return String(data);
}

function isHtmlBody(error, data) {
  const contentType = String(
    error?.response?.headers?.["content-type"] ||
      error?.response?.headers?.["Content-Type"] ||
      "",
  );
  if (/html/i.test(contentType)) return true;
  return typeof data === "string" && /<\s*html|<\s*body|<\s*h1|<\s*center/i.test(data);
}

export function getApiErrorMessage(error) {
  if (!error) {
    return "Something went wrong. Please try again.";
  }

  if (
    error.code === "ERR_CANCELED" ||
    error.name === "CanceledError" ||
    error.message === "canceled"
  ) {
    return null;
  }

  const status = error?.response?.status;
  const data = error?.response?.data;
  const url = error?.config?.url || "";
  const where = url ? ` — ${url}` : "";
  const detail = responseDetail(data);
  const html = isHtmlBody(error, data);

  if (status >= 500) {
    if (html) {
      return `HTTP ${status}${where}. The server returned an HTML error page. ${detail}`.replace(/\s+/g, " ").trim();
    }
    return detail || "The server encountered an error. Please try again shortly.";
  }

  if (status === 401) {
    return detail || "Your session has expired. Please sign in again.";
  }

  if (status === 403) {
    return detail || "You do not have permission to perform this action.";
  }

  if (error.response) {
    if (html) {
      return `HTTP ${status}${where}. The server returned an HTML page, not a CMS error. ${detail}`.replace(/\s+/g, " ").trim();
    }
    return detail || "An error occurred. Please try again.";
  }

  if (error.request || error.message === "Network Error") {
    return `${error.message || "Network Error"}${where}. No response body was received.`;
  }

  return error.message || "Something went wrong. Please try again.";
}
